import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as DM from '@radix-ui/react-dropdown-menu';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Building2, ChevronDown, ChevronRight, Command, Home, KeyRound, LogOut, Moon, Palette, PanelLeftClose, PanelLeftOpen, Search, Sun, X } from 'lucide-react';
import { api } from '@/lib/api';
import type { MenuNode } from '@/lib/types';
import { useAuth } from '@/auth/AuthContext';
import { MenuTree } from './MenuTree';
import { TabIdContext, useWorkspace, WorkspaceProvider, type Tab } from './workspace';
import { ScreenHost } from '@/screens/ScreenHost';
import { Spinner, ErrorBox } from '@/ui/Spinner';
import { cn } from '@/lib/cn';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { CommandPalette } from './CommandPalette';
import { AppearanceDialog } from './Appearance';
import { groupIcon, leafIcon } from './menu';
import { useTheme } from '@/theme/ThemeProvider';
import { BrandIcon } from './BrandIcon';

const Dashboard = lazy(() => import('@/screens/Dashboard'));

export function AppShell() {
  const { user } = useAuth();
  return (
    <WorkspaceProvider storageKey={`dolphin.tabs.${user!.company}.${user!.userName}`} singleInstance={true}>
      <Tooltip.Provider delayDuration={150}>
        <Shell />
      </Tooltip.Provider>
    </WorkspaceProvider>
  );
}

const tagOfTab = (t?: Tab | null) => (t ? (t.kind === 'screen' ? t.tag : t.props?.screen?.tag) ?? null : null);

function Shell() {
  const ws = useWorkspace();
  const theme = useTheme();
  const layout = theme.design.layout;
  const [pwOpen, setPwOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [lookOpen, setLookOpen] = useState(false);
  const menu = useQuery({ queryKey: ['menu'], queryFn: () => api<MenuNode[]>('/menu'), staleTime: 60_000 });
  const activeTab = ws.tabs.find((t) => t.id === ws.active) ?? null;
  const activeTag = tagOfTab(activeTab);

  const openNode = (n: MenuNode) => {
    if (n.kind === 'rapor-grup') ws.open({ kind: 'report-group', title: `Raporlar · ${n.title}`, reportGroup: n.reportGroup });
    else if (n.tag) ws.open({ kind: 'screen', title: n.title, tag: n.tag });
  };

  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLocaleLowerCase('en') === 'k') { e.preventDefault(); setCmdOpen((o) => !o); }
    };
    addEventListener('keydown', f);
    return () => removeEventListener('keydown', f);
  }, []);
  useEffect(() => {
    const f = () => setLookOpen(true);
    addEventListener('dolphin:gorunum', f);
    return () => removeEventListener('dolphin:gorunum', f);
  }, []);

  const nodes = menu.data ?? [];
  const menuState = menu.isLoading ? <div className="p-4 text-[var(--side-muted)]">Menü yükleniyor…</div> : menu.error ? <div className="p-4 text-[var(--danger-text)]">{(menu.error as Error).message}</div> : null;
  const userMenu = <UserMenu onLook={() => setLookOpen(true)} onPw={() => setPwOpen(true)} variant={layout === 'rail' ? 'avatar' : 'full'} />;
  const actions = (
    <div className="flex shrink-0 items-center gap-1.5 pr-2">
      <SearchButton onClick={() => setCmdOpen(true)} wide={layout === 'rail'} />
      <IconBtn tip={theme.dark ? 'Açık mod' : 'Koyu mod'} onClick={() => theme.save({ mod: theme.dark ? 'acik' : 'koyu' })}>{theme.dark ? <Sun size={16} /> : <Moon size={16} />}</IconBtn>
      <IconBtn tip="Görünüm ve tasarım" onClick={() => setLookOpen(true)}><Palette size={16} /></IconBtn>
      {layout !== 'rail' && userMenu}
    </div>
  );
  const content = (
    <div className="relative min-h-0 flex-1">
      {ws.active == null && <Suspense fallback={<Spinner />}><Dashboard menu={nodes} onOpenNode={openNode} /></Suspense>}
      {ws.tabs.map((t) => (
        <div key={t.id} className={cn('absolute inset-0 flex min-w-0 flex-col', ws.active === t.id ? '' : 'hidden')}>
          <TabIdContext.Provider value={t.id}>
            <PageHead tab={t} nodes={nodes} />
            <ScreenHost tab={t} />
          </TabIdContext.Provider>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {layout === 'topnav' ? (
        <div className="flex h-full flex-col">
          <header className="app-topnav no-print flex h-14 shrink-0 items-center gap-4 px-4">
            <Brand compact />
            <TopNav nodes={nodes} onOpen={openNode} activeTag={activeTag} />
            <div className="flex-1" />
            {actions}
          </header>
          <TabsBar />
          {content}
        </div>
      ) : layout === 'rail' ? (
        <RailLayout nodes={nodes} menuState={menuState} onOpen={openNode} activeTag={activeTag} onCmd={() => setCmdOpen(true)} userMenu={userMenu}>
          <header className="app-header no-print flex shrink-0 items-stretch">
            <TabsBar inline />
            {actions}
          </header>
          {content}
        </RailLayout>
      ) : (
        <SidebarLayout nodes={nodes} menuState={menuState} onOpen={openNode} activeTag={activeTag} actions={actions}>
          {content}
        </SidebarLayout>
      )}
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} menu={nodes} onOpenNode={openNode} onAppearance={() => setLookOpen(true)} />
      <AppearanceDialog open={lookOpen} onOpenChange={setLookOpen} />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </>
  );
}

/* ───────── parçalar ───────── */

/** Sekme üstündeki ince sayfa başlığı: menü yolu + başlık */
function PageHead({ tab, nodes }: { tab: Tab; nodes: MenuNode[] }) {
  const tag = tagOfTab(tab);
  const path = useMemo(() => {
    const find = (list: MenuNode[], trail: MenuNode[]): MenuNode[] | null => {
      for (const n of list) {
        if ((tag != null && n.tag === tag && !n.children) || (tab.kind === 'report-group' && n.reportGroup === tab.reportGroup)) return [...trail, n];
        if (n.children) { const r = find(n.children, [...trail, n]); if (r) return r; }
      }
      return null;
    };
    return find(nodes, []) ?? [];
  }, [nodes, tag, tab.kind, tab.reportGroup]);
  const top = path[0];
  const Icon = top?.children ? groupIcon(top) : leafIcon(tab.kind === 'report' || tab.kind === 'report-group' ? 'rapor-grup' : 'ozel');
  return (
    <div className="page-head no-print flex shrink-0 items-center gap-3 px-4">
      <span className="page-head-ic grid h-8 w-8 shrink-0 place-items-center"><Icon size={16} /></span>
      <div className="min-w-0">
        {path.length > 1 && <div className="page-crumbs truncate">{path.slice(0, -1).map((n) => n.title).join(' › ')}</div>}
        <h1 className="page-title h-display truncate">{tab.title}</h1>
      </div>
    </div>
  );
}

function Brand({ compact }: { compact?: boolean }) {
  const { user } = useAuth();
  const ws = useWorkspace();
  return (
    <button className="brand flex min-w-0 items-center gap-2.5 text-left" onClick={() => ws.activate(null)} title="Ana sayfa">
      <span className="brand-mark grid h-9 w-9 shrink-0 place-items-center"><BrandIcon /></span>
      <span className={cn('min-w-0', compact && 'hidden 2xl:block')}>
        <span className="brand-name block truncate text-[14px] font-bold leading-tight">{user?.firm?.unvan?.trim() || 'Dolphin Oto Servis'}</span>
        <span className="brand-sub block truncate text-[11px]">{user?.companyName}</span>
      </span>
    </button>
  );
}

function IconBtn({ tip, onClick, children, active }: { tip: string; onClick?: () => void; children: React.ReactNode; active?: boolean }) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <button onClick={onClick} aria-label={tip} className={cn('icon-btn grid h-9 w-9 place-items-center', active && 'is-on')}>{children}</button>
      </Tooltip.Trigger>
      <Tooltip.Portal><Tooltip.Content sideOffset={6} className="tip z-[80]">{tip}</Tooltip.Content></Tooltip.Portal>
    </Tooltip.Root>
  );
}

function SearchButton({ onClick, wide }: { onClick: () => void; wide?: boolean }) {
  return (
    <button onClick={onClick} className={cn('search-btn flex h-9 items-center gap-2 px-3 text-[13px]', wide ? 'w-64 max-md:w-9 max-md:px-0 max-md:justify-center' : 'w-9 justify-center px-0 xl:w-56 xl:justify-start xl:px-3')} aria-label="Ara (Ctrl+K)">
      <Search size={15} className="shrink-0" />
      <span className={cn('flex-1 truncate text-left', wide ? 'max-md:hidden' : 'hidden xl:block')}>Ara ya da komut…</span>
      <span className={cn('kbd items-center gap-0.5', wide ? 'flex max-md:hidden' : 'hidden xl:flex')}><Command size={9} />K</span>
    </button>
  );
}

export const openSirketler = (ws: ReturnType<typeof useWorkspace>) => ws.open({ kind: 'custom', component: 'SirketTanimlari', title: 'Şirket tanımları', props: { persist: true } });

function UserMenu({ onLook, onPw, variant }: { onLook: () => void; onPw: () => void; variant: 'full' | 'avatar' }) {
  const { user, logout } = useAuth();
  const ws = useWorkspace();
  const theme = useTheme();
  const ini = (user?.displayName || user?.userName || '?').split(/\s+/).map((s) => s[0]).slice(0, 2).join('').toLocaleUpperCase('tr-TR');
  return (
    <DM.Root>
      <DM.Trigger className={cn('user-btn flex items-center gap-2', variant === 'avatar' ? 'p-1' : 'h-9 pl-1 pr-2')} data-testid="kullanici-menu">
        <span className="avatar grid h-7 w-7 place-items-center text-[11px] font-bold">{ini}</span>
        {variant === 'full' && <><span className="hidden max-w-36 truncate text-[13px] font-medium md:block">{user?.displayName}</span><ChevronDown size={14} className="muted hidden md:block" /></>}
      </DM.Trigger>
      <DM.Portal>
        <DM.Content align="end" side={variant === 'avatar' ? 'right' : 'bottom'} sideOffset={8} className="menu z-[60] min-w-60 p-1.5">
          <div className="px-2.5 py-2">
            <div className="font-semibold">{user?.displayName}</div>
            <div className="text-xs muted">{user?.companyName} · {user?.userName}{user?.flags?.Yonetici ? ' · Yönetici' : ''}</div>
          </div>
          <DM.Separator className="my-1 h-px bg-[var(--border)]" />
          <DM.Item className="menu-item" onSelect={onLook}><Palette size={15} />Görünüm · <span className="muted">{theme.design.ad}</span></DM.Item>
          <DM.Item className="menu-item" onSelect={() => theme.save({ mod: theme.dark ? 'acik' : 'koyu' })}>{theme.dark ? <Sun size={15} /> : <Moon size={15} />}{theme.dark ? 'Açık mod' : 'Koyu mod'}</DM.Item>
          <DM.Item className="menu-item" onSelect={onPw}><KeyRound size={15} />Şifre değiştir</DM.Item>
          {user?.flags?.Yonetici && <DM.Item className="menu-item" onSelect={() => openSirketler(ws)}><Building2 size={15} />Şirket tanımları</DM.Item>}
          <DM.Separator className="my-1 h-px bg-[var(--border)]" />
          <DM.Item className="menu-item text-[var(--danger-text)]" onSelect={() => logout()}><LogOut size={15} />Çıkış</DM.Item>
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}

function TabsBar({ inline }: { inline?: boolean }) {
  const ws = useWorkspace();
  return (
    <div className={cn('tabsbar no-print flex min-w-0 items-end gap-1 overflow-x-auto px-2 scroll-thin', inline ? 'flex-1' : 'shrink-0')}>
      <button className={cn('tab tab-home flex shrink-0 items-center gap-1.5 px-3', ws.active == null && 'is-active')} onClick={() => ws.activate(null)} title="Ana sayfa" data-testid="ana-sayfa">
        <Home size={14} /><span className="hidden sm:inline">Ana sayfa</span>
      </button>
      {ws.tabs.map((t) => (
        <div key={t.id} data-tab={t.id} data-active={ws.active === t.id || undefined}
          className={cn('tab group flex max-w-60 shrink-0 cursor-pointer items-center gap-1.5 pl-3 pr-1.5', ws.active === t.id && 'is-active')}
          onClick={() => ws.activate(t.id)} onAuxClick={(e) => e.button === 1 && ws.close(t.id)} title={t.title}>
          <span className="truncate">{t.title}</span>
          <button className="tab-x grid h-5 w-5 place-items-center" onClick={(e) => { e.stopPropagation(); ws.close(t.id); }} aria-label="Kapat"><X size={12} /></button>
        </div>
      ))}
    </div>
  );
}

function SidebarLayout({ nodes, menuState, onOpen, activeTag, actions, children }: {
  nodes: MenuNode[]; menuState: React.ReactNode; onOpen: (n: MenuNode) => void; activeTag: number | null; actions: React.ReactNode; children: React.ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(() => typeof innerWidth !== 'undefined' && innerWidth < 900);
  const { user } = useAuth();
  const theme = useTheme();
  return (
    <div className="flex h-full">
      <aside className={cn('app-side no-print flex shrink-0 flex-col transition-[width] duration-200', collapsed ? 'w-0 overflow-hidden border-r-0' : 'w-[var(--side-w)]')}>
        <div className="px-4 pb-3 pt-4"><Brand /></div>
        <div className="min-h-0 flex-1">{menuState ?? <MenuTree nodes={nodes} onOpen={onOpen} activeTag={activeTag} />}</div>
        <div className="side-foot m-3 flex items-center gap-2.5 p-2.5">
          <span className="avatar grid h-8 w-8 shrink-0 place-items-center text-[11px] font-bold">{(user?.displayName || '?').split(/\s+/).map((s) => s[0]).slice(0, 2).join('').toLocaleUpperCase('tr-TR')}</span>
          <div className="min-w-0 flex-1"><div className="truncate text-[13px] font-semibold text-[var(--side-fg-strong,var(--side-fg))]">{user?.displayName}</div><div className="truncate text-[11px] text-[var(--side-muted)]">{theme.design.ad} · {user?.flags?.Yonetici ? 'Yönetici' : 'Kullanıcı'}</div></div>
          <button className="side-icon-btn grid h-8 w-8 place-items-center" title="Görünüm" onClick={() => dispatchEvent(new Event('dolphin:gorunum'))}><Palette size={15} /></button>
        </div>
      </aside>
      <main className="flex min-w-0 flex-1 flex-col">
        <header className="app-header no-print flex shrink-0 items-stretch">
          <button className="icon-btn my-auto ml-2 grid h-9 w-9 shrink-0 place-items-center" onClick={() => setCollapsed((c) => !c)} title="Menüyü gizle/göster" aria-label="Menüyü gizle/göster">
            {collapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
          </button>
          <TabsBar inline />
          {actions}
        </header>
        {children}
      </main>
    </div>
  );
}

const RAIL_LS = 'dolphin.railGroup';
function RailLayout({ nodes, menuState, onOpen, activeTag, onCmd, userMenu, children }: {
  nodes: MenuNode[]; menuState: React.ReactNode; onOpen: (n: MenuNode) => void; activeTag: number | null; onCmd: () => void; userMenu: React.ReactNode; children: React.ReactNode;
}) {
  const ws = useWorkspace();
  const [group, setGroup] = useState<string | null>(() => { try { return localStorage.getItem(RAIL_LS); } catch { return null; } });
  const sel = nodes.find((n) => n.key === group) ?? null;
  const pick = (k: string | null) => { setGroup(k); try { k ? localStorage.setItem(RAIL_LS, k) : localStorage.removeItem(RAIL_LS); } catch { /* */ } };
  const containsTag = (n: MenuNode): boolean => n.tag === activeTag || !!n.children?.some(containsTag);
  return (
    <div className="flex h-full">
      <nav className="app-rail no-print flex w-[60px] shrink-0 flex-col items-center gap-1 py-3">
        <button className="brand-mark mb-3 grid h-9 w-9 place-items-center" onClick={() => ws.activate(null)} title="Ana sayfa"><BrandIcon /></button>
        <RailBtn tip="Ana sayfa" on={ws.active == null && !sel} onClick={() => { pick(null); ws.activate(null); }}><Home size={18} /></RailBtn>
        {nodes.map((n) => {
          const I = n.children ? groupIcon(n) : leafIcon(n.kind);
          return <RailBtn key={n.key} tip={n.title} on={sel?.key === n.key} mark={activeTag != null && containsTag(n)} onClick={() => (n.children ? pick(sel?.key === n.key ? null : n.key) : onOpen(n))}><I size={18} /></RailBtn>;
        })}
        <div className="flex-1" />
        <RailBtn tip="Ara (Ctrl+K)" onClick={onCmd}><Search size={18} /></RailBtn>
        {userMenu}
      </nav>
      {sel && (
        <aside className="app-side no-print flex w-[var(--side-w)] shrink-0 flex-col">
          <div className="flex items-center gap-2 px-4 pb-2 pt-4">
            <span className="flex-1 truncate text-[13px] font-semibold text-[var(--side-fg-strong,var(--text))]">{sel.title}</span>
            <button className="side-icon-btn grid h-7 w-7 place-items-center" onClick={() => pick(null)} title="Paneli kapat"><ChevronRight size={15} className="rotate-180" /></button>
          </div>
          <div className="min-h-0 flex-1">{menuState ?? <MenuTree key={sel.key} nodes={sel.children ?? []} onOpen={onOpen} activeTag={activeTag} />}</div>
        </aside>
      )}
      <main className="flex min-w-0 flex-1 flex-col">{children}</main>
    </div>
  );
}

function RailBtn({ tip, on, mark, onClick, children }: { tip: string; on?: boolean; mark?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <button onClick={onClick} aria-label={tip} className={cn('rail-btn relative grid h-10 w-10 place-items-center', on && 'is-on', mark && 'has-active')}>{children}</button>
      </Tooltip.Trigger>
      <Tooltip.Portal><Tooltip.Content side="right" sideOffset={10} className="tip z-[80]">{tip}</Tooltip.Content></Tooltip.Portal>
    </Tooltip.Root>
  );
}

function TopNav({ nodes, onOpen, activeTag }: { nodes: MenuNode[]; onOpen: (n: MenuNode) => void; activeTag: number | null }) {
  const containsTag = (n: MenuNode): boolean => n.tag === activeTag || !!n.children?.some(containsTag);
  const items = (list: MenuNode[]) => list.map((c) => c.children ? (
    <DM.Sub key={c.key}>
      <DM.SubTrigger className="menu-item">{c.title}<ChevronRight size={14} className="ml-auto muted" /></DM.SubTrigger>
      <DM.Portal><DM.SubContent sideOffset={4} className="menu z-[60] max-h-[70vh] min-w-56 overflow-y-auto p-1.5 scroll-thin">{items(c.children)}</DM.SubContent></DM.Portal>
    </DM.Sub>
  ) : (
    <DM.Item key={c.key} className={cn('menu-item', c.tag === activeTag && 'is-active')} onSelect={() => onOpen(c)}>{c.title}{c.shortcut && <span className="kbd ml-auto">{c.shortcut}</span>}</DM.Item>
  ));
  return (
    <nav className="topnav hidden min-w-0 items-stretch gap-0.5 self-stretch overflow-x-auto lg:flex">
      {nodes.map((n) => n.children ? (
        <DM.Root key={n.key} modal={false}>
          <DM.Trigger className={cn('topnav-item flex shrink-0 items-center gap-1 whitespace-nowrap px-2 text-[13px] font-medium', activeTag != null && containsTag(n) && 'is-active')}>
            {n.title}<ChevronDown size={13} className="opacity-60" />
          </DM.Trigger>
          <DM.Portal><DM.Content align="start" sideOffset={2} className="menu z-[60] max-h-[75vh] min-w-60 overflow-y-auto p-1.5 scroll-thin">{items(n.children)}</DM.Content></DM.Portal>
        </DM.Root>
      ) : (
        <button key={n.key} className="topnav-item shrink-0 whitespace-nowrap px-2 text-[13px] font-medium" onClick={() => onOpen(n)}>{n.title}</button>
      ))}
    </nav>
  );
}

export { Spinner, ErrorBox };
